/**
 * Which hostname each environment should actually be served from (PIX-628).
 *
 * Firebase Hosting gives every site a `<site>.web.app` and
 * `<site>.firebaseapp.com` default domain, and those cannot be turned off. So
 * each environment is reachable at two hostnames serving the identical build,
 * which costs us three things:
 *
 *   - The blog is prerendered at build time so articles rank (PIX-573). Served
 *     from a second host, every article exists at two indexable URLs
 *     competing with each other.
 *   - localStorage and cookies are per-origin, and `web.app` is a different
 *     registrable domain from `sortmyprep.com`, so no cookie can span them.
 *     The same visitor on both hosts is two different PEOPLE in analytics,
 *     with two device ids and two sessions. Confirmed in the staging project,
 *     which holds events under both hostnames.
 *   - Access and refresh tokens live in localStorage, so someone signed in on
 *     one hostname is signed out on the other.
 *
 * This has to be decided in the app rather than in `firebase.json`, whose
 * `redirects` match on path only - there is no host condition.
 *
 * Pure and dependency-free so the decision is unit tested rather than trusted
 * to a hostname string typed once into a bootstrap file. Getting the guard
 * wrong in the unsafe direction makes a review build permanently unreachable.
 */

/** Firebase default hostname -> the domain it should be served from. */
export const CANONICAL_HOSTS = Object.freeze({
  'studentportal-prod.web.app': 'sortmyprep.com',
  'studentportal-prod.firebaseapp.com': 'sortmyprep.com',
  'studentportal-staging.web.app': 'staging.sortmyprep.com',
  'studentportal-staging.firebaseapp.com': 'staging.sortmyprep.com',
});

/**
 * Hostnames that must never be redirected, whatever else matches.
 *
 * The dev site is deliberately absent from CANONICAL_HOSTS rather than listed
 * here: it has no custom domain, so its default hostname IS its canonical one.
 */
const NEVER_REDIRECT = ['localhost', '127.0.0.1', '[::1]', '::1'];

/**
 * True for a Firebase preview channel URL.
 *
 * Preview channels are `<site>--<channel>-<hash>.web.app`. They are how review
 * builds get looked at, and redirecting one would send the reviewer to
 * production and quietly hide the thing they were asked to review. The `--`
 * is the marker Firebase itself uses, and no canonical host contains one.
 */
export function isPreviewChannel(hostname) {
  return typeof hostname === 'string' && hostname.includes('--');
}

/**
 * The URL this request should be sent to, or null to stay put.
 *
 * Takes a location-shaped object rather than reading `window`, so the whole
 * matrix is testable. Path, query and hash are preserved: a redirect that
 * dropped them would turn a shared deep link into a homepage visit, which is
 * a worse bug than the one being fixed.
 */
export function canonicalRedirectUrl(location) {
  if (!location || typeof location !== 'object') return null;

  const { hostname, pathname = '/', search = '', hash = '' } = location;
  if (typeof hostname !== 'string' || !hostname) return null;
  if (NEVER_REDIRECT.includes(hostname)) return null;
  if (isPreviewChannel(hostname)) return null;

  const target = CANONICAL_HOSTS[hostname.toLowerCase()];
  if (!target || target === hostname) return null;

  // Always https. These hosts are HTTPS-only, and carrying the current
  // protocol across would let an http:// visit stay unencrypted.
  return `https://${target}${pathname || '/'}${search || ''}${hash || ''}`;
}
